import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react'
import Slider from 'components/Slider'
import SnapCarousel from 'components/SnapCarousel'
import NextImage from 'components/Image'
import { Content } from 'components/elements/styled'
import { useCurrency } from 'hooks/useCurrency'
import { useIsMobile } from 'hooks/useIsMobile'
import * as S from './CustomOfferCard.styled'
import { useAppState } from 'context/AppContext'
import { i18n } from 'next-i18next'
import { CustomOfferCardProps, CustomOfferCardData } from './CustomOfferCard.typed'
import { CustomOfferCard as CustomOfferCardComponent, useAuthContext } from '@enuygun/frontend-components'
import dayjs from 'dayjs'
import { logRecoListView, logRecoItemView, logRecoItemClick } from './CustomOfferCard.tracker'

// Helper function to get anonymous_id
const getAnonymousId = () => {
  if (globalThis.window === undefined) return null

  // 1. Check query param
  const urlParams = new URLSearchParams(globalThis.window.location.search)
  const queryAnonymousId = urlParams.get('anonymous_id')
  if (queryAnonymousId) return queryAnonymousId

  // 2. Check cookie - case insensitive
  const cookieString = document.cookie
  // Try both CCI and cci (case insensitive)
  const match = /(^| )CCI=([^;]+)/i.exec(cookieString)
  if (match) {
    const value = match[2] || match[3]
    return value
  }

  return null
}

const getDateText = (isOneWay: boolean, formattedDepartureDate: string, formattedReturnDate: string | null) => {
  if (isOneWay || !formattedReturnDate) {
    return formattedDepartureDate
  }
  return `${formattedDepartureDate} - ${formattedReturnDate}`
}

// Helper function to convert API flight data to CustomOfferCardData
const convertFlightResponseToCardData = (flight: any, index: number): CustomOfferCardData => {
  // API formatına göre verileri al
  const originCityName = flight.origin?.cityName || ''
  const destinationCityName = flight.destination?.cityName || ''
  const destinationImage = flight.destination?.image || ''
  const searchUrl = flight.searchUrl || ''
  const departureDateRaw = flight.departureDate || ''
  const returnDateRaw = flight.returnDate || null
  const isOneWay = flight.isOneWay === true

  // Tarih formatlaması - PromotedFlight'teki gibi "22 Aralık 2025" formatında
  const formatDate = (dateString: string) => {
    if (!dateString) return ''
    return dayjs(dateString).format('D MMMM YYYY')
  }

  const formattedDepartureDate = formatDate(departureDateRaw)
  const formattedReturnDate = returnDateRaw ? formatDate(returnDateRaw) : null

  const routeText = `${originCityName} - ${destinationCityName}`
  const dateText = getDateText(isOneWay, formattedDepartureDate, formattedReturnDate)
  const tripTypeText = isOneWay ? 'Tek Yön' : 'Gidiş-Dönüş'
  const priceSuffix = "'den başlayan fiyatlarla"
  const buttonText = 'Bileti bul'

  return {
    id: `enuygun-homepage-flight-${index}`,
    offerType: 'flight',
    image: destinationImage,
    searchUrl: searchUrl,
    routeText,
    dateText,
    tripTypeText,
    price: null, // Will be set with currency formatter
    priceSuffix,
    buttonText,
    originCity: originCityName,
    destinationCity: destinationCityName,
    sliderId: `enuygun-homepage-flight-slider-${index}`,
    position: 'homepage',
    ranking: index + 1,
  }
}

const CustomOfferCard: React.FC<CustomOfferCardProps> = ({
  title = 'Sana Özel Önerileri Keşfet',
  subtitle = 'Size özel hazırladığımız fırsatları keşfedin',
  tab1Label = 'Uçak bileti',
  tab1Data,
  initialFlightsData,
}) => {
  const { currency } = useAppState()
  const currencyFormatter = useCurrency()
  const isMobile = useIsMobile()
  const authContext = useAuthContext()
  const [loading, setLoading] = useState<boolean>(!initialFlightsData)
  const [fetchedData, setFetchedData] = useState<{
    flights: any[]
    cookieConsentActive: boolean
  }>({
    flights: initialFlightsData || [],
    cookieConsentActive: false,
  })

  const languageCode = i18n?.language?.includes('-') ? i18n.language?.split('-')[1] : i18n?.language
  const isRTL = i18n?.dir(languageCode) === 'rtl'
  const itemViewTrackedRef = useRef<Set<number>>(new Set())
  const listViewTrackedRef = useRef<boolean>(false)
  const itemRefsRef = useRef<Map<number, HTMLButtonElement>>(new Map())
  const observerRef = useRef<IntersectionObserver | null>(null)

  // Fetch recommendations from API - sadece initialFlightsData yoksa
  useEffect(() => {
    // Eğer initialFlightsData varsa (SSR'den geldiyse), tekrar fetch etme
    if (initialFlightsData && initialFlightsData.length > 0) {
      return
    }

    const fetchRecommendations = async () => {
      try {
        const headers: Record<string, string> = {}

        const authInfo = (globalThis as any).__PRELOADED_STATE__?.authTokenInfo
        if (globalThis.window !== undefined && authInfo?.server_auth_header) {
          headers['Authorization'] = authInfo.server_auth_header
        }

        const anonymousId = getAnonymousId()

        const url = new URL('https://personalization.enuygun.com/v1/recommendations')

        // Önce anonymous_id, sonra type ekle
        if (anonymousId) {
          url.searchParams.append('anonymous_id', anonymousId)
        }
        url.searchParams.append('type', 'flight')

        const fullUrl = url.toString()

        const response = await fetch(fullUrl, {
          method: 'GET',
          headers: headers,
          credentials: 'include', // Cookie'leri cross-origin request'lerde gönder
        })

        const data = await response.json()

        // API response kontrolü: success true, flights boş değil
        if (data?.success === true && Array.isArray(data?.flights) && data.flights.length > 0) {
          setFetchedData({
            flights: data.flights,
            cookieConsentActive: data.cookieConsentActive === true,
          })
        } else {
          setFetchedData({
            flights: [],
            cookieConsentActive: data?.cookieConsentActive === true,
          })
        }
      } catch {
      } finally {
        setLoading(false)
      }
    }

    fetchRecommendations()
  }, [initialFlightsData])

  // Convert flight responses to card data from API
  const flightCards = useMemo(() => {
    if (tab1Data) return tab1Data

    // Use only API data - no fallback to mock data
    if (fetchedData.flights.length === 0) {
      return []
    }

    return fetchedData.flights.map((flight, index) => {
      const cardData = convertFlightResponseToCardData(flight, index)

      // Price formatting - conversion varsa display currency (app state) ile sembol göster, yoksa API currency
      let formattedPrice: React.ReactNode = null
      if (flight.price?.amount) {
        const priceAmount = flight.price.amount
        const priceCurrency = flight.price.currency || currency?.iso || 'TRY'
        const displayCurrency = currency?.iso || priceCurrency
        formattedPrice = currencyFormatter?.conversion ? currencyFormatter.summaryFormatHtml(priceAmount, 0, false, true, displayCurrency) : `${priceAmount} ${priceCurrency}`
      }

      return {
        ...cardData,
        price: formattedPrice ? <div dangerouslySetInnerHTML={{ __html: formattedPrice as string }}></div> : null,
      }
    })
  }, [tab1Data, fetchedData.flights, currencyFormatter, currency])

  const hasFlightData = flightCards && flightCards.length > 0

  // Log reco_list_view when component renders with flight data
  useEffect(() => {
    if (!loading && hasFlightData && flightCards && flightCards.length > 0 && !listViewTrackedRef.current) {
      const flights = tab1Data ? [] : fetchedData.flights
      if (flights.length > 0) {
        logRecoListView({
          flights: flights,
          title: title,
          isMobile: !!isMobile,
          authContext: authContext,
          language: i18n?.language,
        })
        listViewTrackedRef.current = true
      }
    }
  }, [loading, hasFlightData, flightCards, title, isMobile, authContext, tab1Data, fetchedData.flights])

  // Intersection Observer for item view tracking
  useEffect(() => {
    if (globalThis.window === undefined || !hasFlightData || !flightCards || flightCards.length === 0) return

    // Cleanup previous observer
    if (observerRef.current) {
      observerRef.current.disconnect()
    }

    // Create intersection observer
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = Number.parseInt((entry.target as HTMLElement).dataset.itemIndex || '-1', 10)
            if (index >= 0 && !itemViewTrackedRef.current.has(index)) {
              const flight = tab1Data ? null : fetchedData.flights[index]
              if (flight) {
                logRecoItemView({
                  flight: flight,
                  itemPosition: index + 1,
                  listPosition: 1,
                  totalItems: fetchedData.flights.length,
                  isMobile: !!isMobile,
                  authContext: authContext,
                  language: i18n?.language,
                })
                itemViewTrackedRef.current.add(index)
              }
            }
          }
        })
      },
      {
        threshold: 0.5, // Item is considered viewed when 50% visible
      }
    )

    // Observe all item refs after a short delay to ensure refs are set
    setTimeout(() => {
      itemRefsRef.current.forEach((ref) => {
        if (ref && observerRef.current) {
          observerRef.current.observe(ref)
        }
      })
    }, 100)

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect()
      }
    }
  }, [hasFlightData, flightCards, isMobile, authContext, tab1Data, fetchedData.flights])

  // Handle item click
  const handleItemClick = useCallback(
    (index: number) => {
      const flight = tab1Data ? null : fetchedData.flights[index]
      if (flight) {
        logRecoItemClick({
          flight: flight,
          itemPosition: index + 1,
          listPosition: 1,
          totalItems: fetchedData.flights.length,
          isMobile: !!isMobile,
          authContext: authContext,
          language: i18n?.language,
        })
      }
    },
    [tab1Data, fetchedData.flights, isMobile, authContext]
  )

  // Auth kontrolü - kullanıcı login ise veya CCI cookie'si varsa component render edilir
  const checkAuth = () => {
    // 1. useAuthContext ile token kontrolü
    const hasToken = authContext?.token && authContext.token.trim().length > 0

    // 2. User kontrolü - userId veya id varsa login olmuştur
    const user = authContext?.user
    const hasUserId = user && (('userId' in user && user.userId) || ('id' in user && user.id))

    // 3. isAuthenticated kontrolü
    const isAuthenticated = user && 'isAuthenticated' in user && user.isAuthenticated === true

    // 4. User objesi boş değilse ve içinde anlamlı property varsa login olmuştur
    const hasUserProperties = user && Object.keys(user).length > 0 && !(Object.keys(user).length === 1 && 'login_method' in user)

    // 5. window.__PRELOADED_STATE__ ile authTokenInfo kontrolü
    let hasPreloadedAuth = false
    const authInfo = (globalThis as any).__PRELOADED_STATE__?.authTokenInfo
    if (globalThis.window !== undefined && authInfo?.server_auth_header) {
      hasPreloadedAuth = authInfo.server_auth_header.trim().length > 0
    }

    return hasToken || hasUserId || isAuthenticated || hasUserProperties || hasPreloadedAuth
  }

  // CCI cookie kontrolü
  const hasCCI = () => {
    if (globalThis.window === undefined) return false
    const cookieString = document.cookie
    const match = /(^| )CCI=([^;]+)/i.exec(cookieString)
    return !!(match && (match[2] || match[3]))
  }

  const isLoggedIn = checkAuth()
  const hasCCICookie = hasCCI()
  const hasCookieConsent = fetchedData.cookieConsentActive
  const shouldRender = isLoggedIn || hasCCICookie || hasCookieConsent

  // Dynamic subtitle
  const displaySubtitle = subtitle === 'Size özel hazırladığımız fırsatları keşfedin' ? 'Keşfedebileceğin Uçuşlar' : subtitle

  // Login değilse, CCI cookie yoksa ve cookieConsentActive true değilse component render etme
  if (!shouldRender) {
    return null
  }

  // Loading durumunda component render etme
  if (loading) {
    return null
  }

  // API'den veri yoksa component render etme
  if (!hasFlightData) {
    return null
  }

  const renderCards = () =>
    flightCards?.map((cardData, index) => {
      const setItemRef = (el: HTMLButtonElement | null) => {
        if (el) {
          itemRefsRef.current.set(index, el)
        } else {
          itemRefsRef.current.delete(index)
        }
      }

      return (
        <React.Fragment key={cardData.id}>
          <button
            type="button"
            ref={setItemRef}
            data-item-index={index}
            onClick={() => handleItemClick(index)}
            style={{
              cursor: 'pointer',
              background: 'none',
              border: 'none',
              padding: 0,
              margin: 0,
              width: '100%',
              textAlign: 'inherit',
              font: 'inherit',
              color: 'inherit',
              display: 'block',
            }}
          >
            <CustomOfferCardComponent
              id={cardData.id}
              offerType={cardData.offerType}
              image={cardData.image}
              searchUrl={cardData.searchUrl}
              routeText={cardData.routeText}
              dateText={cardData.dateText}
              tripTypeText={cardData.tripTypeText}
              price={cardData.price}
              priceSuffix={cardData.priceSuffix}
              buttonText={cardData.buttonText}
              originCity={cardData.originCity}
              destinationCity={cardData.destinationCity}
              isChristmas={cardData.isChristmas}
              sliderId={cardData.sliderId}
              position={cardData.position}
              ranking={cardData.ranking}
              rating={cardData.rating}
              ratingText={cardData.ratingText}
            />
          </button>
        </React.Fragment>
      )
    })

  return (
    <S.Container isFlightTab={true} isHotelTab={false}>
      <NextImage
        alt="Custom offer flight background"
        src={
          isMobile
            ? 'https://cdn2.enuygun.com/media/lib/uploads/image/flight-customoffercard-mobile-70186.png'
            : 'https://cdn2.enuygun.com/media/lib/uploads/image/flight-customoffercard-70160.png'
        }
        style={{ backgroundRepeat: 'no-repeat', backgroundPosition: 'center' }}
        decoding="async"
        loading="lazy"
        fill
      />
      <Content>
        <S.HeaderContainer>
          <S.HeaderWrapper>
            <S.Title data-testid="custom-offer-title">{title}</S.Title>
            <S.Subtitle data-testid="custom-offer-subtitle">{displaySubtitle}</S.Subtitle>
          </S.HeaderWrapper>
        </S.HeaderContainer>
        <S.CardsWrapper data-testid="custom-offer-wrapper" isFlightTab={true}>
          {isMobile ? (
            <SnapCarousel
              isRTL={isRTL}
              options={{
                perPage: 1.25,
                gap: 16,
              }}
              dataTestId="custom-offer-carousel"
              containerWidth={1120}
              isMobile={true}
            >
              {renderCards()}
            </SnapCarousel>
          ) : (
            <Slider
              options={{
                arrows: true,
                pagination: true,
                fixedWidth: undefined,
                gap: 15,
                perPage: 4,
                padding: 0,
              }}
            >
              {renderCards()}
            </Slider>
          )}
        </S.CardsWrapper>
      </Content>
    </S.Container>
  )
}

export default CustomOfferCard
